import React from "react";

import { FileProps, FilePropsWithTypeId, UploadFileStatuses } from "../Types";
import { useAsyncEffect } from "../../../../../../../../Common/UseAsyncEffect";
import { CustomFile, selectUserFiles } from "../../../../../../Helpers/file";
import { uuid } from "../../../../../../../../Common/UUID";

import { ButtonType, UploadFilesContainer } from "./UploadFilesContainer";

interface UploadFileProps<T> {
    filterValidDriveUrl: (fileToRemove: FileProps, value: T) => boolean;
    checkValidFile: (
        file: FileProps,
        setValidDriveURLs: React.Dispatch<React.SetStateAction<T[]>>
    ) => Promise<FileProps | FilePropsWithTypeId>;
    multiple?: boolean;
    buttonType?: ButtonType;
    ref?: React.ForwardedRef<HTMLElement>;
    getPortalUserId?: () => Promise<string>;
    useExternDriveClient?: boolean;
}

const createUploadFileProps = (driveFiles: CustomFile[]): FileProps[] =>
    driveFiles.map(file => {
        const uid = uuid();
        return { id: uid, uploadFile: file, status: UploadFileStatuses.new };
    });

export function useUploadFile<ValidFilesType>({
    filterValidDriveUrl,
    checkValidFile,
    multiple,
    buttonType,
    ref,
    getPortalUserId,
    useExternDriveClient,
}: UploadFileProps<ValidFilesType>) {
    const [uploadedFiles, setUploadedFiles] = React.useState<FileProps[]>([]);
    const [validDriveURLs, setValidDriveURLs] = React.useState<ValidFilesType[]>([]);

    useAsyncEffect(async () => {
        uploadedFiles.forEach(async file => {
            if (file.status === UploadFileStatuses.new && file.driveUri) {
                await checkFile(file);
            }
        });
    }, [uploadedFiles]);

    const checkFile = async (file: FileProps): Promise<void> => {
        setNewFileState({
            id: file.id,
            uploadFile: file.uploadFile,
            status: UploadFileStatuses.progress,
            driveUri: file.driveUri,
        });
        const newFileState = await checkValidFile(file, setValidDriveURLs);
        setNewFileState(newFileState);
    };

    const removeFileHandler = (fileToRemove: FileProps) => {
        setUploadedFiles(prevState => prevState.filter(file => file.id !== fileToRemove.id));
        setValidDriveURLs(prevState => prevState.filter(value => filterValidDriveUrl(fileToRemove, value)));
    };

    const setNewFileState = (nextFileProps: FileProps) => {
        setUploadedFiles(prev =>
            prev.map(file => {
                if (file.uploadFile === nextFileProps.uploadFile) {
                    return nextFileProps;
                }
                return file;
            })
        );
    };

    const addUserFilesToDrive = async (multipleOption: boolean = true): Promise<void> => {
        await selectUserFiles(multipleOption).then(value => {
            if (value.length) {
                setUploadedFiles(prev => [...prev, ...createUploadFileProps(value)]);
            }
        });
    };

    const filesContainer = (
        <UploadFilesContainer
            pathToTable={""}
            setNewFileProps={setNewFileState}
            files={uploadedFiles}
            removeFileHandler={removeFileHandler}
            addUserFilesToDrive={addUserFilesToDrive}
            multiple={multiple ?? true}
            buttonType={buttonType}
            ref={ref}
            getPortalUserId={getPortalUserId}
            useExternDriveClient={useExternDriveClient}
        />
    );

    return {
        uploadedFiles: uploadedFiles,
        filesContainer: filesContainer,
        validDriveURLs: validDriveURLs,
        addUserFilesToDrive: addUserFilesToDrive,
    };
}
